// Predefined Kolam designs for tracing and coloring activities.
// Each design uses SVG paths so we can scale, color, and render to PNG.

export const KOLAM_TRACE = {
  // Simple 3x3 dot grid kolam — perfect for first-time tracers.
  viewBox: "0 0 400 400",
  dots: [
    [100, 100], [200, 100], [300, 100],
    [100, 200], [200, 200], [300, 200],
    [100, 300], [200, 300], [300, 300],
  ],
  // Guide curves that thread around each dot — drawn faintly as a trace guide.
  guides: [
    "M 60 100 Q 100 60 140 100 Q 100 140 60 100 Z",
    "M 160 100 Q 200 60 240 100 Q 200 140 160 100 Z",
    "M 260 100 Q 300 60 340 100 Q 300 140 260 100 Z",
    "M 60 200 Q 100 160 140 200 Q 100 240 60 200 Z",
    "M 160 200 Q 200 160 240 200 Q 200 240 160 200 Z",
    "M 260 200 Q 300 160 340 200 Q 300 240 260 200 Z",
    "M 60 300 Q 100 260 140 300 Q 100 340 60 300 Z",
    "M 160 300 Q 200 260 240 300 Q 200 340 160 300 Z",
    "M 260 300 Q 300 260 340 300 Q 300 340 260 300 Z",
  ],
};

// A lotus-mandala kolam for the coloring activity.
// Each region has an id we can map to a fill color.
export const KOLAM_COLOR_REGIONS = [
  { id: "center", d: "M 200 200 m -28 0 a 28 28 0 1 0 56 0 a 28 28 0 1 0 -56 0", label: "Lotus heart" },
  // 8 inner petals (around center)
  { id: "petal1", d: "M 200 172 Q 220 130 200 90 Q 180 130 200 172 Z", label: "Petal North" },
  { id: "petal2", d: "M 228 200 Q 270 180 310 200 Q 270 220 228 200 Z", label: "Petal East" },
  { id: "petal3", d: "M 200 228 Q 220 270 200 310 Q 180 270 200 228 Z", label: "Petal South" },
  { id: "petal4", d: "M 172 200 Q 130 180 90 200 Q 130 220 172 200 Z", label: "Petal West" },
  { id: "petal5", d: "M 220 180 Q 256 150 286 114 Q 250 144 220 180 Z", label: "Petal NE" },
  { id: "petal6", d: "M 220 220 Q 256 250 286 286 Q 250 256 220 220 Z", label: "Petal SE" },
  { id: "petal7", d: "M 180 220 Q 144 250 114 286 Q 150 256 180 220 Z", label: "Petal SW" },
  { id: "petal8", d: "M 180 180 Q 144 150 114 114 Q 150 144 180 180 Z", label: "Petal NW" },
  // Outer dot ring
  { id: "dot1", d: "M 200 60 m -10 0 a 10 10 0 1 0 20 0 a 10 10 0 1 0 -20 0", label: "Dot N" },
  { id: "dot2", d: "M 340 200 m -10 0 a 10 10 0 1 0 20 0 a 10 10 0 1 0 -20 0", label: "Dot E" },
  { id: "dot3", d: "M 200 340 m -10 0 a 10 10 0 1 0 20 0 a 10 10 0 1 0 -20 0", label: "Dot S" },
  { id: "dot4", d: "M 60 200 m -10 0 a 10 10 0 1 0 20 0 a 10 10 0 1 0 -20 0", label: "Dot W" },
  { id: "dot5", d: "M 300 100 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0", label: "Dot NE" },
  { id: "dot6", d: "M 300 300 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0", label: "Dot SE" },
  { id: "dot7", d: "M 100 300 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0", label: "Dot SW" },
  { id: "dot8", d: "M 100 100 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0", label: "Dot NW" },
];

export const KOLAM_PALETTE = [
  { name: "Marigold", value: "#F5B400" },
  { name: "Terracotta", value: "#D95D39" },
  { name: "Rose Pink", value: "#E54B6A" },
  { name: "Lotus Red", value: "#C03546" },
  { name: "Indigo", value: "#1E2A4F" },
  { name: "Sky", value: "#3FA9F5" },
  { name: "Leaf Green", value: "#5BA152" },
  { name: "Lime", value: "#C6D63A" },
  { name: "Sandalwood", value: "#E2A95F" },
  { name: "Cream", value: "#F9F6F0" },
];

export const KOLAM_FACTS = [
  {
    title: "What is a Kolam?",
    body:
      "A kolam is a beautiful pattern drawn on the floor at the entrance of a home — usually with rice flour, chalk, or coloured powder. It's a quiet little ritual of welcome.",
    emoji: "🌸",
  },
  {
    title: "Where did it begin?",
    body:
      "Kolams come from South India, especially Tamil Nadu, where families have been drawing them at sunrise for over 5,000 years. Grandmothers teach granddaughters, who teach their daughters.",
    emoji: "🌅",
  },
  {
    title: "Why dots?",
    body:
      "Every kolam starts with a grid of dots called pulli. The artist draws long curving lines that loop around each dot — never lifting her finger, never crossing the line.",
    emoji: "•",
  },
  {
    title: "What does it mean?",
    body:
      "Kolams welcome guests, feed ants and birds (because rice flour is food!), and remind us that something beautiful can be made fresh every single day — then quietly swept away.",
    emoji: "🐜",
  },
];
